import { useState } from 'react';
import { initialsOf } from '../lib/initials';

interface AvatarProps {
  name: string;
  profile_image_url?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function Avatar({
  name,
  profile_image_url,
  size = 'md',
  className = ''
}: AvatarProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Size configurations
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg'
  };

  // Generate initials from name (shared with the landing page's demo faces so
  // the mock cannot drift from the real fallback — see lib/initials.ts).
  const initials = initialsOf(name);

  // Default background color for initials fallback
  // user_level removed (#785) - using consistent muted gradient
  const backgroundColor = 'bg-gradient-to-br from-oro-border to-oro-surface-alt';

  // Show image if we have a src, haven't errored, and want to attempt loading
  const showImage = profile_image_url && !imageError;
  // Show initials if we don't have an image src, image failed to load, or image hasn't loaded yet
  const showInitials = !profile_image_url || imageError || (!imageLoaded && showImage);

  const avatarClasses = `
    ${sizeClasses[size]} 
    ${className}
    rounded-full 
    relative
    flex items-center justify-center 
    font-semibold text-white 
    border-2
    border-transparent
    transition-all duration-200
    overflow-hidden
  `.trim();

  return (
    <div className={avatarClasses}>
      {/* Image */}
      {showImage && (
        <img
          src={profile_image_url}
          alt={`${name}'s avatar`}
          className="absolute inset-0 w-full h-full object-cover"
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setImageError(true);
            setImageLoaded(false);
          }}
        />
      )}
      
      {/* Initials fallback */}
      {showInitials && (
        <div
          className={`
            absolute inset-0 flex items-center justify-center
            ${backgroundColor}
            ${showImage && !imageLoaded ? 'opacity-50' : ''}
          `}
        >
          {initials}
        </div>
      )}
    </div>
  );
}